import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// ── Tipos (tarea 16.2.2 y 16.2.3) ─────────────────────────────────────────────
export type NotificationType = 'info' | 'advertencia' | 'error' | 'exito'
export type NotificationPriority = 'baja' | 'media' | 'alta' | 'critica'

export interface Notification {
  id: string
  type: NotificationType
  priority: NotificationPriority
  title: string
  message: string
  timestamp: string   // ISO string
  read: boolean
  /** Opcional: ruta o acción para "ver detalle" */
  action?: { label: string; route?: string; fn?: () => void }
  /** Para agrupación (tarea 16.4.3) */
  group?: string
}

const STORAGE_KEY = 'notifications-history'
const MAX_STORED = 50

// ── Store ──────────────────────────────────────────────────────────────────────
export const useNotificationsStore = defineStore('notifications', () => {
  // ── Estado ─────────────────────────────────────────────────────────────────
  const notifications = ref<Notification[]>(loadFromStorage())

  // ── Computed ────────────────────────────────────────────────────────────────
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)

  const criticalCount = computed(
    () => notifications.value.filter((n) => n.priority === 'critica' && !n.read).length,
  )

  /** Notificaciones agrupadas por `group` (para las que tienen group) */
  const grouped = computed(() => {
    const groups: Record<string, Notification[]> = {}
    const ungrouped: Notification[] = []
    for (const n of notifications.value) {
      if (n.group) {
        if (!groups[n.group]) groups[n.group] = []
        groups[n.group].push(n)
      } else {
        ungrouped.push(n)
      }
    }
    return { groups, ungrouped }
  })

  // ── Filtros por tipo ────────────────────────────────────────────────────────
  const activeFilter = ref<NotificationType | 'todas'>('todas')

  const filtered = computed(() => {
    const all = notifications.value
    if (activeFilter.value === 'todas') return all
    return all.filter((n) => n.type === activeFilter.value)
  })

  // ── Preferencias (tarea 16.7.1) ─────────────────────────────────────────────
  const preferences = ref(loadPreferences())

  function loadPreferences() {
    try {
      return JSON.parse(localStorage.getItem('notification-preferences') ?? 'null') ?? {
        info: true,
        advertencia: true,
        error: true,
        exito: true,
      }
    } catch {
      return { info: true, advertencia: true, error: true, exito: true }
    }
  }

  // ── Acciones ────────────────────────────────────────────────────────────────

  /** Añade una nueva notificación (tarea 16.2) */
  function push(payload: Omit<Notification, 'id' | 'timestamp' | 'read'>) {
    // Respetar preferencias del usuario
    if (!preferences.value[payload.type]) return

    const n: Notification = {
      ...payload,
      id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date().toISOString(),
      read: false,
    }
    notifications.value.unshift(n)
    // Limitar a MAX_STORED
    if (notifications.value.length > MAX_STORED) {
      notifications.value = notifications.value.slice(0, MAX_STORED)
    }
    persist()
  }

  function markRead(id: string) {
    const n = notifications.value.find((x) => x.id === id)
    if (n) { n.read = true; persist() }
  }

  function markAllRead() {
    notifications.value.forEach((n) => (n.read = true))
    persist()
  }

  function dismiss(id: string) {
    notifications.value = notifications.value.filter((n) => n.id !== id)
    persist()
  }

  function clearAll() {
    notifications.value = []
    persist()
  }

  function setFilter(type: NotificationType | 'todas') {
    activeFilter.value = type
  }

  function savePreferences() {
    localStorage.setItem('notification-preferences', JSON.stringify(preferences.value))
  }

  // ── Persistencia ──────────────────────────────────────────────────────────
  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.value))
    } catch {}
  }

  return {
    notifications,
    unreadCount,
    criticalCount,
    grouped,
    filtered,
    activeFilter,
    preferences,
    push,
    markRead,
    markAllRead,
    dismiss,
    clearAll,
    setFilter,
    savePreferences,
  }
})

function loadFromStorage(): Notification[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
  } catch {
    return []
  }
}
